import React from 'react';
import { usePlayer } from '../context/PlayerContext';
import { Play, Pause, Loader2, Volume2 } from 'lucide-react';
import { TEST_IDS } from '../constants/testIds';

const ICON_SIZE_LARGE = 44;
const ICON_SIZE_SMALL = 22;
const VOLUME_PERCENT = 100;

const renderPlayIcon = (loading, playing, iconSize) => {
  if (loading) return <Loader2 size={iconSize} className="animate-spin" />;
  if (playing) return <Pause size={iconSize} fill="currentColor" />;
  return <Play size={iconSize} fill="currentColor" className="ml-1" />;
};

const statusLabel = (playing, isLive) => {
  if (playing) return 'Now Broadcasting';
  if (isLive) return 'Live · Tap To Tune In';
  return 'Off Air';
};

export default function Player({ size = 'lg' }) {
  const { playing, loading, toggle, volume, setVolume, nowPlaying } = usePlayer();

  const btnSize = size === 'lg' ? 'w-28 h-28 md:w-36 md:h-36' : 'w-16 h-16';
  const iconSize = size === 'lg' ? ICON_SIZE_LARGE : ICON_SIZE_SMALL;

  return (
    <div className="w-full flex flex-col items-center text-center">
      {/* Big circular play button */}
      <button
        data-testid={TEST_IDS.playerToggle}
        onClick={toggle}
        aria-label={playing ? 'Pause OSO London stream' : 'Play OSO London stream'}
        className={`${btnSize} hairline rounded-full flex items-center justify-center bg-white text-black hover:bg-white hover:text-black transition-colors duration-300`}
      >
        {renderPlayIcon(loading, playing, iconSize)}
      </button>

      {/* Status row */}
      <div className="mt-6 flex items-center justify-center gap-3 text-[11px] font-mono-r tracking-[0.35em] uppercase">
        <span
          data-testid={TEST_IDS.playerLiveDot}
          className={`inline-block w-2.5 h-2.5 bg-black ${playing ? 'pulse-dot' : 'opacity-40'}`}
        />
        <span>{statusLabel(playing, nowPlaying.is_live)}</span>
      </div>

      {/* VU bars when playing */}
      {playing && (
        <div className="mt-6 flex items-end justify-center h-8" aria-hidden>
          <span className="vu-bar" /><span className="vu-bar" /><span className="vu-bar" /><span className="vu-bar" /><span className="vu-bar" />
        </div>
      )}

      {/* Now playing meta */}
      <div className="mt-8 max-w-md w-full hairline px-4 py-4">
        <div className="text-[10px] font-mono-r tracking-[0.4em] uppercase opacity-60 mb-2">Now Playing</div>
        <div
          data-testid={TEST_IDS.playerSongTitle}
          className="font-display text-2xl md:text-3xl leading-tight break-words"
        >
          {nowPlaying.song || 'Live Broadcast'}
        </div>
        <div className="mt-3 flex items-center justify-between text-[10px] font-mono-r tracking-[0.3em] uppercase opacity-70">
          <span data-testid={TEST_IDS.playerListeners}>LISTENERS {String(nowPlaying.listeners).padStart(3,'0')}</span>
          <span>{nowPlaying.bitrate} KBPS</span>
        </div>
      </div>

      {/* Volume */}
      <div className="mt-6 flex items-center gap-3 w-full max-w-md">
        <Volume2 size={16} />
        <input
          data-testid={TEST_IDS.playerVolume}
          type="range"
          min="0" max="1" step="0.01"
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
          className="flex-1 accent-white"
          aria-label="Volume"
        />
        <span className="text-[10px] font-mono-r tracking-[0.25em] w-8 text-right">{Math.round(volume * VOLUME_PERCENT)}</span>
      </div>
    </div>
  );
}
